import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate, STATUS_BADGE } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { Plus, Trash2, Pencil, ArrowDownRight, ListChecks, Receipt, Scale } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const PIE_COLORS = ["#0A1633", "#C9A227", "#2563eb", "#059669", "#dc2626", "#7c3aed", "#0891b2", "#d97706"];
const emptyForm = () => ({
  category: "", amount: "", supplier_name: "", description: "",
  payment_method: "Bank BCA", payment_status: "Lunas", date: today(),
});

// Compact cell overrides
const th = "h-8 px-1.5 text-[11px] font-semibold whitespace-nowrap";
const td = "px-1.5 py-1.5 text-xs";

export default function Expenses() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("expenses");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [omzet, setOmzet] = useState(0);
  const [opts, setOpts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;
  const load = () => {
    api.get(`/transactions?type=expense&${qstr()}`).then((r) => setRows(r.data));
    api.get(`/expenses/stats?${qstr()}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/transactions/income-stats?${qstr()}`).then((r) => setOmzet(r.data.total_omzet || 0)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => { api.get("/meta/options").then((r) => setOpts(r.data)); }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t._id);
    setForm({
      category: t.category || "", amount: String(t.amount || ""), supplier_name: t.supplier_name || "",
      description: t.description || "", payment_method: t.payment_method || "Kas Tunai",
      payment_status: t.payment_status || "Lunas", date: (t.date || today()).slice(0, 10),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.category || !form.amount) { toast.error("Kategori dan jumlah wajib diisi"); return; }
    const body = {
      category: form.category,
      amount: parseFloat(form.amount),
      base_price: parseFloat(form.amount),
      supplier_name: form.supplier_name || null,
      description: form.description,
      payment_method: form.payment_method,
      payment_status: form.payment_status,
      date: form.date,
    };
    try {
      if (editId) {
        await api.patch(`/transactions/${editId}`, body);
        toast.success("Pengeluaran diperbarui");
      } else {
        await api.post("/transactions", { type: "expense", ...body });
        toast.success("Pengeluaran dicatat");
      }
      setOpen(false); setEditId(null); setForm(emptyForm()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "transaction", resource_id: id, label: "pengeluaran" });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Pengeluaran dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const total = stats ? stats.total : 0;
  const cashflow = omzet - total;

  const statCards = [
    { id: "stat-total-expense", label: "Total Pengeluaran", icon: ArrowDownRight, iconCls: "text-rose-600", bg: "bg-rose-100 dark:bg-rose-950", main: formatIDR(total) },
    { id: "stat-expense-count", label: "Jumlah Transaksi", icon: ListChecks, iconCls: "text-sky-600", bg: "bg-sky-100 dark:bg-sky-950", main: String(stats ? stats.count : 0) },
    { id: "stat-top-category", label: "Kategori Terbesar", icon: Receipt, iconCls: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-950", main: stats && stats.by_category[0] ? stats.by_category[0].category : "-", sub: stats && stats.by_category[0] ? formatIDR(stats.by_category[0].amount) : "" },
    { id: "stat-cashflow", label: "Arus Kas Bersih", icon: Scale, iconCls: cashflow >= 0 ? "text-emerald-600" : "text-rose-600", bg: cashflow >= 0 ? "bg-emerald-100 dark:bg-emerald-950" : "bg-rose-100 dark:bg-rose-950", main: formatIDR(cashflow), mainCls: cashflow >= 0 ? "text-emerald-600" : "text-rose-600", sub: `Omzet ${formatIDR(omzet)} − Keluar ${formatIDR(total)}` },
  ];

  const fieldRow = (label, value) => (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs text-right break-words min-w-0">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Pengeluaran</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Seluruh uang keluar — terpisah dari order masuk/penjualan.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn" size="sm" className="gap-1.5 h-8 text-xs shrink-0" onClick={openCreate}><Plus className="h-3.5 w-3.5" /> Pengeluaran</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-base">{editId ? "Edit Pengeluaran" : "Catat Pengeluaran"}</DialogTitle>
                <DialogDescription className="text-xs">{editId ? "Perbarui detail pengeluaran yang salah input." : "Catat uang keluar — terpisah dari order masuk."}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <div className="space-y-1">
                  <Label className="text-xs">Kategori Pengeluaran</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="exp-category" className="h-9 text-xs"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                    <SelectContent>{(opts?.expense_categories || []).map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Jumlah (Rp)</Label>
                  <Input data-testid="exp-amount" type="number" className="h-9 text-xs" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Penerima / Supplier (opsional)</Label>
                  <Input data-testid="exp-supplier" className="h-9 text-xs" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="mis. CV Mitra / Nama karyawan" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Sumber Dana</Label>
                    <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                      <SelectTrigger data-testid="exp-method" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Status</Label>
                    <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                      <SelectTrigger data-testid="exp-status" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts?.payment_status.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tanggal</Label>
                  <Input data-testid="exp-date" type="date" className="h-9 text-xs" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Keterangan</Label>
                  <Textarea data-testid="exp-description" rows={2} className="text-xs" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opsional" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={submit} data-testid="exp-submit" variant="gold" size="sm" className="h-8 text-xs">{editId ? "Simpan Perubahan" : "Simpan Pengeluaran"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="expense-stats">
        {statCards.map((s) => (
          <Card key={s.id} data-testid={s.id} className="p-4">
            <div className="flex items-center gap-2.5">
              <div className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${s.bg}`}><s.icon className={`h-4.5 w-4.5 ${s.iconCls}`} style={{ width: 18, height: 18 }} /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">{s.label}</p>
                <p className={`font-heading text-base font-extrabold leading-tight truncate ${s.mainCls || ""}`}>{s.main}</p>
                {s.sub && <p className="text-[10px] text-muted-foreground truncate">{s.sub}</p>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <Card className="p-4 xl:col-span-1" data-testid="expense-pie-card">
          <h3 className="font-heading text-sm font-semibold mb-1">Komposisi Pengeluaran</h3>
          {!stats || stats.by_category.length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center">Belum ada data pengeluaran.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={stats.by_category} dataKey="amount" nameKey="category" cx="50%" cy="45%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {stats.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="overflow-hidden xl:col-span-3">
          {/* Desktop / tablet-wide table (lg and up) */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={th}>Tanggal</TableHead>
                  <TableHead className={th}>Kategori</TableHead>
                  <TableHead className={th}>Penerima</TableHead>
                  <TableHead className={th}>Keterangan</TableHead>
                  <TableHead className={th}>Sumber Dana</TableHead>
                  <TableHead className={th}>Status</TableHead>
                  <TableHead className={th}>Oleh</TableHead>
                  <TableHead className={`${th} text-right`}>Jumlah</TableHead>
                  <TableHead className={`${th} w-16 text-right`}>Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground text-xs py-8">Belum ada pengeluaran pada periode ini.</TableCell></TableRow>
                )}
                {rows.map((t) => (
                  <TableRow key={t._id} data-testid={`exp-row-${t._id}`}>
                    <TableCell className={`${td} whitespace-nowrap text-muted-foreground`}>{formatDate(t.date)}</TableCell>
                    <TableCell className={`${td} font-medium whitespace-nowrap`}>{t.category}</TableCell>
                    <TableCell className={`${td} text-muted-foreground whitespace-nowrap`}>{t.supplier_name || "-"}</TableCell>
                    <TableCell className={`${td} text-muted-foreground max-w-[160px] whitespace-normal break-words`}>{t.description || "-"}</TableCell>
                    <TableCell className={`${td} text-muted-foreground whitespace-nowrap`}>{t.payment_method}</TableCell>
                    <TableCell className={td}><Badge variant="outline" className={`${STATUS_BADGE[t.payment_status]} text-[10px] px-1.5 py-0`}>{t.payment_status}</Badge></TableCell>
                    <TableCell className={`${td} text-muted-foreground whitespace-nowrap`}>{t.created_by_name}</TableCell>
                    <TableCell className={`${td} text-right font-semibold text-rose-600 whitespace-nowrap`}>- {formatIDR(t.amount)}</TableCell>
                    <TableCell className={`${td} text-right`}>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEdit(t)} data-testid={`exp-edit-${t._id}`} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {t.pending_deletion ? (
                          <span data-testid={`exp-pending-${t._id}`} className="text-[9px] font-semibold text-amber-600 px-1.5 self-center">Menunggu persetujuan</span>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(t._id)} data-testid={`exp-delete-${t._id}`} title="Hapus">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile / tablet-narrow: stacked cards (all fields, no horizontal scroll) */}
          <div className="lg:hidden divide-y divide-border">
            {rows.length === 0 && (
              <p className="text-center text-muted-foreground text-xs py-8">Belum ada pengeluaran pada periode ini.</p>
            )}
            {rows.map((t) => (
              <div key={t._id} data-testid={`exp-row-${t._id}`} className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{t.category}</p>
                    <p className="text-[11px] text-muted-foreground">{formatDate(t.date)}</p>
                  </div>
                  <p className="text-sm font-bold text-rose-600 whitespace-nowrap">- {formatIDR(t.amount)}</p>
                </div>
                <div className="space-y-0.5">
                  {fieldRow("Penerima", t.supplier_name || "-")}
                  {fieldRow("Keterangan", t.description || "-")}
                  {fieldRow("Sumber Dana", t.payment_method)}
                  {fieldRow("Oleh", t.created_by_name)}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Status</span>
                    <Badge variant="outline" className={`${STATUS_BADGE[t.payment_status]} text-[10px] px-1.5 py-0`}>{t.payment_status}</Badge>
                  </div>
                </div>
                <div className="flex justify-end gap-1 pt-0.5">
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => openEdit(t)} data-testid={`exp-edit-${t._id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  {t.pending_deletion ? (
                    <span data-testid={`exp-pending-mobile-${t._id}`} className="text-[10px] font-semibold text-amber-600 self-center px-1">Menunggu persetujuan hapus</span>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => remove(t._id)} data-testid={`exp-delete-${t._id}`}>
                      <Trash2 className="h-3 w-3" /> Hapus
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
